import React, { useCallback, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import { bulkSparesTableColumnsConfig } from "./bulkSparesTableColumnDef";

import { Table } from "../../../../component/table/Table";
import { CategoryPageSkeleton } from "../../../../component/skeleton/CategoryPageSkeleton";
import { selectBulkSparesFilterState } from "../../../../store/bulk/bulkSparesFilterSlice";
import {
  selectBulkSpares,
  useGetBulkSpareListQuery,
} from "../../../../services/bulk/spares/bulkSparesApiSlice";
import { cancellationsListSlice } from "../../../../services/cancellation/cancellationApiSlice";
import {
  useApproveBulkDealRequestMutation,
  useRejectBulkDealRequestMutation,
} from "../../../../services/bulk/spares/bulkSpareMutationApiSlice";
import { toast } from "react-toastify";
import { InventorySpareForm } from "../../../../component/inventory/spares/inventorySpareForm/InventorySpareForm";
import { Modal } from "../../../../component/inventory/spares/modal/Modal";
import { bulkDealDownloadRequest } from "../../../../http-request/bulk_deals/bulkDealsDownloadFie";

export const BulkSparesTablePage = () => {
  const navigate = useNavigate();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [rejectRequest] = useRejectBulkDealRequestMutation();
  const [approveRequest] = useApproveBulkDealRequestMutation();

  /* ✅ Get filters from redux */
  const { seller_id, status } = useSelector(selectBulkSparesFilterState);

  /* ✅ Call RTK Query with filters */
  const { isLoading, isError, isSuccess, isFetching } =
    useGetBulkSpareListQuery({
      seller_id,
      status,
    });

  /* ✅ Get normalized data from entity adapter */
  const bulkSpareList = useSelector(selectBulkSpares);

  const handleNavigate = useCallback(
    (request_id) => {
      navigate(`/bulk_deals/spares/${request_id}`);
    },
    [navigate],
  );

  const handleDownload = useCallback(async (requestId) => {
    try {
      toast.success("downloading...");
      const fileData = await bulkDealDownloadRequest({
        category: "spares",
        requestId: requestId,
      });

      const contentType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
      const blob = new Blob([fileData], { type: contentType });

      // Create a temporary link element
      const link = document.createElement("a");
      link.href = window.URL.createObjectURL(blob);
      link.download = `mobiGarage_${requestId}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error occurred during download:", err);
      toast.success("error while downloading");
    }
  }, []);

  const handleOpenModal = useCallback((rowData) => {
    setSelectedRow(rowData);
    setIsModalOpen(true);
  }, []);

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedRow(null);
  };

  const handleReject = async (formData) => {
    if (!selectedRow) return;

    const toastId = toast.loading("Rejecting request...");

    try {
      setIsSubmitting(true);

      const response = await rejectRequest({
        request_id: selectedRow.request_id,
        remarks: formData.remarks,
      }).unwrap();

      toast.update(toastId, {
        render: response?.message?.displayMessage || "Rejected successfully",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });

      handleCloseModal();
    } catch (error) {
      toast.update(toastId, {
        render:
          error?.data?.message?.displayMessage || "Failed to reject request",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  /* -------------------- APPROVE -------------------- */
  const handleApprove = async () => {
    if (!selectedRow) return;

    const toastId = toast.loading("Approving request...");

    try {
      setIsSubmitting(true);

      const response = await approveRequest({
        request_id: selectedRow.request_id,
      }).unwrap();

      toast.update(toastId, {
        render: response?.message?.displayMessage || "Approved successfully",
        type: "success",
        isLoading: false,
        autoClose: 2000,
      });

      handleCloseModal();
    } catch (error) {
      toast.update(toastId, {
        render:
          error?.data?.message?.displayMessage || "Failed to approve request",
        type: "error",
        isLoading: false,
        autoClose: 2000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const columnDefs = useMemo(
    () =>
      bulkSparesTableColumnsConfig(
        handleOpenModal,
        handleDownload,
        handleNavigate,
      ),
    [handleDownload, handleNavigate, handleOpenModal],
  );

  if (isError)
    return (
      <div
        className={cancellationsListSlice.box__error}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          width: "100%",
          color: "#ff6f3f",
          fontFamily: "Poppins, sans-serif",
          fontSize: "16px",
          fontWeight: 500,
          textAlign: "center",
        }}
      >
        Something went wrong
      </div>
    );

  return isSuccess ? (
    <>
      {" "}
      <Table
        data={bulkSpareList}
        columns={columnDefs}
        loading={isLoading}
        isFetching={isFetching}
      />
      <Modal
        isOpen={isModalOpen}
        title="Approve or Reject Spare Request"
        onClose={handleCloseModal}
      >
        <InventorySpareForm
          initialValues={selectedRow}
          onSubmit={handleReject}
          onApprove={handleApprove}
          isSubmitting={isSubmitting}
        />
      </Modal>
    </>
  ) : (
    <CategoryPageSkeleton />
  );
};
